'use client';

import type { EmblaCarouselType } from 'embla-carousel';
import { useState } from 'react';

import { Button } from '@/app/components/button';
import { IconArrowBack } from '@/app/components/icons/ui-actions/icon-arrow-back';
import { IconArrowForward } from '@/app/components/icons/ui-actions/icon-arrow-forward';
import { Container } from '@/app/components/layouts/container';
import { Section } from '@/app/components/layouts/section';
import { PaginationDash } from '@/app/components/navigation/pagination';
import { PartItem } from '@/app/components/sections/service/parts/part-item';
import { Sliders } from '@/app/components/sliders';
import { usePaginationSlider } from '@/app/components/sliders/hooks/usePaginationSlider';
import { usePrevNextSlider } from '@/app/components/sliders/hooks/usePrevNextSlider';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { type PartItem as PartItemType } from '@/types/tis-cms/types';

interface RelatedPartsProps {
  items: PartItemType[];
  cardUrl?: string;
  testId?: string;
}

export const RelatedParts = ({
  items = [],
  testId = 'related-parts',
}: RelatedPartsProps) => {
  const [emblaApi, setEmblaApi] = useState<EmblaCarouselType | null>(null);
  const { currentPage, onPageChange, scrollSnaps } =
    usePaginationSlider(emblaApi);

  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = usePrevNextSlider(emblaApi);

  const handlePageChange = (page: number) => {
    if (!emblaApi) return;
    emblaApi.scrollTo(page - 1);
  };

  if (!items?.length) return null;

  return (
    <Section
      className="bg-silver-50"
      {...createDataAttributes({ test: `section-${testId}` })}
    >
      <Container>
        <div className="mb-8 flex items-end justify-between sm:mb-10">
          <h2 className="text-h3 font-display">รายการแนะนำอื่นๆ</h2>
          {items.length > 1 && (!prevBtnDisabled || !nextBtnDisabled) && (
            <div className="hidden gap-x-6 md:flex">
              <Button
                aria-label="Previous"
                variant="primary"
                color="black"
                iconBefore={<IconArrowBack />}
                onClick={onPrevButtonClick}
                disabled={prevBtnDisabled}
                {...createDataAttributes({ test: `${testId}-prev-button` })}
              />
              <Button
                aria-label="Next"
                variant="primary"
                color="black"
                iconBefore={<IconArrowForward />}
                onClick={onNextButtonClick}
                disabled={nextBtnDisabled}
                {...createDataAttributes({ test: `${testId}-next-button` })}
              />
            </div>
          )}
        </div>
        <Sliders
          onEmblaInit={setEmblaApi}
          align="start"
          containerClassName="-ml-4 lg:-ml-6"
          {...createDataAttributes({ test: `${testId}-slider` })}
        >
          {items.map((item, index) => (
            <div
              key={index}
              className={cn('flex-none basis-1/2 lg:basis-1/3 xl:basis-1/4')}
              {...createDataAttributes({ test: `${testId}-slide-${index}` })}
            >
              <PartItem
                index={index}
                partImage={item?.partImage}
                headlineTitle={item?.headlineTitle}
                genuineParts={item?.genuineParts}
                url={item?.url}
                testId={testId}
                dataTrack={testId}
                className="h-full pl-6"
              />
            </div>
          ))}
        </Sliders>
        {items.length > 1 && scrollSnaps.length > 1 && (
          <PaginationDash
            theme="dark"
            wrapperClassName="mt-8 md:mt-10 justify-center"
            totalPages={scrollSnaps.length}
            currentPage={currentPage}
            onPageChange={onPageChange}
          />
        )}
      </Container>
    </Section>
  );
};
